import { API_URL } from "../config";

// =============================================================================
// Mesure d'audience web — collecte côté client (cookieless, RGPD-safe).
//
// Envoie un page_view à POST /v1/web/track via navigator.sendBeacon (non
// bloquant, survit à la navigation/unload). On n'envoie JAMAIS d'identifiant :
// le serveur dérive un hash de visiteur du jour (salt rotatif) à partir de l'IP
// et du user-agent qu'il lit lui-même, puis les jette. Aucun cookie, aucun
// localStorage posé sur le terminal.
//
// Tous les thèmes (Ummah / 2026 / Travel / Campagne) passent par _app.js, donc
// ce service les couvre tous depuis un point d'injection unique.
// =============================================================================

const TRACK_URL = API_URL + "/web/track";

// On ne track pas : page d'erreur, rendu dans l'iframe d'un tiers, ou absence d'org.
export function isTrackable(pageProps) {
    if (!pageProps) return false;
    if (pageProps.errorPage) return false;
    // Embed demandé par l'URL (`fromUrl`) = la page vit dans le site de quelqu'un
    // d'autre, on ne compte pas. Embed posé par la SEULE config de l'org = c'est
    // SON site, servi sans chrome : il faut compter, sinon elle perd 100 % de ses
    // statistiques le jour où elle coche la case.
    // Les deux se cumulent — une org en mode intégré dont le client colle le code
    // fourni (qui ajoute `?render=embed`) est bien dans une iframe tierce : c'est
    // `fromUrl` qui tranche, jamais `forced`. Sans embed transmis (page d'erreur),
    // on ne compte pas : c'est le comportement historique.
    if (pageProps.render === "embed" && (pageProps.embed?.fromUrl !== false)) return false;
    if (!pageProps.org || !pageProps.org.id) return false;
    return true;
}

// L'app mobile marque blanche ouvre le site dans une WebView et injecte, AVANT
// le 1er rendu, window.APP_MOBILE_CONFIG_ID (l'id de la mobile_config) +
// window.ISAPP + window.APP_PLATFORM (cf. shell natif unisoft_mobile,
// INJECTED_JS dans src/config.js). On remonte ces drapeaux au serveur pour que
// le trafic de l'app NE soit PAS confondu avec le trafic web classique : côté
// API, la simple présence de mobileConfigId bascule l'event sur le canal "app".
// Absent (navigateur normal) → null → canal "web" inchangé.
function appContext() {
    if (typeof window === "undefined") return {mobileConfigId: null, isApp: false, appPlatform: null};
    const id = window.APP_MOBILE_CONFIG_ID;
    return {
        mobileConfigId: typeof id === "string" && id ? id : null,
        isApp: window.ISAPP === true,
        appPlatform: typeof window.APP_PLATFORM === "string" ? window.APP_PLATFORM : null,
    };
}

export function buildPayload(pageProps) {
    const app = appContext();
    return {
        org: pageProps.org.id,
        // Pour les sites touristiques, on rattache l'event au site Travel précis
        // (une org peut en héberger plusieurs). L'API expose l'id du site sous
        // `org.travel.siteId` (cf. services/travel.js getTravelData) ; `.id` n'a
        // jamais existé sur cet objet → l'event partait avec siteId:null et les
        // stats Travel (filtrées par siteId) restaient à zéro.
        siteId: pageProps.org?.travel?.siteId || pageProps.org?.travel?.id || null,
        type: "page_view",
        // pathname courant (toujours à jour, même sur navigation SPA)
        path: typeof window !== "undefined" ? window.location.pathname : "/",
        pageType: pageProps.page?.type || null,
        theme: pageProps.config?.theme || null,
        lang: pageProps.langValue || null,
        isMobile: !!pageProps.isMobile,
        referrer: typeof document !== "undefined" ? document.referrer : "",
        // Segmentation app marque blanche vs web classique (cf. appContext).
        mobileConfigId: app.mobileConfigId,
        isApp: app.isApp,
        appPlatform: app.appPlatform,
    };
}

// Envoi non bloquant et SANS cookie.
//
// ⚠️ CORS : l'API répond `Access-Control-Allow-Origin: *`. Le navigateur
// interdit le wildcard `*` dès qu'une requête inclut des credentials (cookies).
// Or `navigator.sendBeacon` envoie TOUJOURS les cookies (impossible à
// désactiver) → bloqué cross-origin. On privilégie donc `fetch` avec
// `credentials: "omit"`. De plus on envoie le body en `text/plain` : c'est un
// Content-Type "simple" qui n'entraîne PAS de requête preflight OPTIONS
// (contrairement à application/json). Le serveur parse ce text/plain en JSON.
function send(payload) {
    const body = JSON.stringify(payload);

    // 1) fetch keepalive sans credentials (chemin nominal, compatible wildcard).
    try {
        fetch(TRACK_URL, {
            method: "POST",
            headers: { "Content-Type": "text/plain;charset=UTF-8" },
            body,
            keepalive: true,
            credentials: "omit",
            mode: "cors",
        }).catch(() => {});
        return;
    } catch (e) {
        // fetch indisponible : dernier recours ci-dessous.
    }

    // 2) Dernier recours : sendBeacon (envoie les cookies, peut être bloqué en
    // cross-origin, mais vaut mieux que rien sur de très vieux navigateurs).
    try {
        if (navigator.sendBeacon) {
            navigator.sendBeacon(TRACK_URL, new Blob([body], { type: "text/plain" }));
        }
    } catch (e) {
        // Silencieux : la mesure d'audience ne doit jamais casser la page.
    }
}

// Point d'entrée unique. SSR-safe (no-op côté serveur).
export function track(pageProps) {
    if (typeof navigator === "undefined") return;
    if (!isTrackable(pageProps)) return;
    send(buildPayload(pageProps));
}

export default { track, isTrackable, buildPayload };
